import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from "react";
import { useAuth } from "@/app";
import { updateUserProfile } from "@/shared/api/auth";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
import { isWebPaymentEnv } from "@/shared/lib/paymentNavigation";
import { BillingEmailModal } from "../ui/BillingEmailModal/BillingEmailModal";
/**
 * Перед созданием платежа бэк требует billing_email (54-ФЗ, отправка чека).
 * У пользователей из мини-аппа почты часто нет → POST /payments отдаёт 400.
 * Этот хук закрывает кейс: спрашивает почту, сохраняет в профиль и отдаёт
 * её вызывающему, чтобы продолжить стандартный флоу оплаты.
 */
export function useBillingEmailGate() {
    const { user, refreshUser } = useAuth();
    const [opened, setOpened] = useState(false);
    const resolverRef = useRef(null);
    const ensureBillingEmail = useCallback(() => {
        if (user?.billing_email && user.billing_email.trim()) {
            return Promise.resolve({ ok: true });
        }
        setOpened(true);
        return new Promise((resolve) => {
            resolverRef.current = resolve;
        });
    }, [user?.billing_email]);
    const handleSubmit = useCallback(async (email) => {
        // Вкладку оплаты открываем ПРЯМО ЗДЕСЬ — это ещё пользовательский жест
        // (сабмит модалки), поэтому window.open не блокируется. Делаем это ДО
        // любого await, иначе жест «протухнет». На клике «Оплатить» вкладку не
        // открывали (почты не было), так что пустой вкладки, пока юзер вводит
        // почту, больше нет. В мини-аппах вкладку не открываем (оплата — openLink).
        const payWindow = isWebPaymentEnv()
            ? window.open("about:blank", "_blank")
            : null;
        try {
            // Бросаем дальше при ошибке — модалка покажет сообщение и не закроется.
            await updateUserProfile({ billing_email: email });
        }
        catch (e) {
            // Сохранение не удалось — закрываем открытую вкладку, чтобы не висела.
            payWindow?.close();
            throw e;
        }
        // Введена billing-почта перед оплатой (дошли до сохранения = валидна).
        trackEvent(AnalyticsEvent.BILLING_EMAIL_ENTERED, { valid: true });
        // Подтянуть обновлённый профиль, чтобы следующие оплаты не спрашивали.
        try {
            await refreshUser();
        }
        catch {
            /* не критично: email уже сохранён, ниже всё равно передадим его в платёж */
        }
        setOpened(false);
        resolverRef.current?.({ ok: true, billingEmail: email, payWindow });
        resolverRef.current = null;
    }, [refreshUser]);
    const handleClose = useCallback(() => {
        setOpened(false);
        resolverRef.current?.({ ok: false });
        resolverRef.current = null;
    }, []);
    const billingEmailModal = (_jsx(BillingEmailModal, { opened: opened, onClose: handleClose, onSubmit: handleSubmit, defaultEmail: user?.email }));
    return { ensureBillingEmail, billingEmailModal };
}
